import { useLoaderData } from 'react-router';

import type { Route } from './+types/content';
import type { Content } from '@prismicio/client';
import { getPrismicClient } from '@/cms/client.server';
import { contentPageFor } from '@/cms/content-pages';
import { metaFor } from '@/cms/meta';
import { Footer } from '@/components/footer';
import { AppHeader } from '@/components/header/app-header';
import { S9FormWidget } from '@/components/s9-form-widget';
import { config } from '@/config.server';
import { useStrings } from '@/i18n/use-strings';
import GeneralContent from '@/slices/GeneralContent';

/** A document of any of the five single types. They are field-identical. */
type ContentDocument =
  | Content.AboutDocument
  | Content.ResourcesDocument
  | Content.ContactDocument
  | Content.PrivacyPolicyDocument
  | Content.TermsofuseDocument;

/**
 * The five content pages are one route module: same chrome, same slice zone,
 * one Prismic single type each. `contentPageFor` resolves the request path to
 * its type — the path is also the canonical URL and the sitemap entry, so there
 * is one table rather than a route id per page and a second list beside it.
 */
export async function loader({ request }: Route.LoaderArgs) {
  const page = contentPageFor(new URL(request.url).pathname);

  if (!page) {
    throw new Response(null, { status: 404, statusText: 'Not Found' });
  }

  const client = getPrismicClient(request);

  let doc: ContentDocument;

  try {
    doc = await client.getSingle(page.type);
  } catch {
    // An unpublished (or unreachable) page is a 404 rather than a 500: there is
    // nothing to render and nothing to index.
    throw new Response(null, { status: 404, statusText: 'Not Found' });
  }

  return {
    path: page.path,
    lede: page.lede,
    linkCards: page.linkCards,
    doc,
    /**
     * Only the contact page embeds a form. Both values come from `APP_SECRETS`
     * and are blank locally, which the widget renders as a friendly fallback.
     */
    contactForm:
      page.type === 'contact'
        ? {
            formUrl: config.secrets.APP_S9_CONTACT_US_FORM_URL,
            scriptUrl: config.secrets.APP_S9_FORM_SERVICE_BASE_URL
              ? `${config.secrets.APP_S9_FORM_SERVICE_BASE_URL.replace(/\/+$/, '')}/scripts/embed-form/widget-0.1.0.min.js`
              : undefined,
          }
        : null,
  };
}

export function meta({ data, matches }: Route.MetaArgs) {
  const siteSettings = matches[0]?.data.siteSettings;

  if (!siteSettings || !data) {
    return [{ title: 'Beach Passport' }];
  }

  return metaFor({ siteSettings, path: data.path }, data.doc);
}

export default function ContentPage() {
  const { doc, lede, linkCards, contactForm } = useLoaderData<typeof loader>();
  const { content } = useStrings();

  const title = doc.data.meta_title?.trim();

  return (
    <>
      <AppHeader variant="full" />

      <main id="main-content" className="mx-auto w-full max-w-2xl flex-1 px-4 py-8 sm:px-6 sm:py-12">
        <article className="text-surf-dark-blue rounded-3xl bg-white p-6 shadow-[0_18px_50px_-24px_rgb(2_20_66_/_0.55)] sm:p-10">
          {title ? (
            <h1 className="font-bebas mb-6 text-4xl leading-[1.05] tracking-wide text-balance uppercase sm:text-5xl">
              {title}
            </h1>
          ) : null}

          <div className="space-y-8">
            {doc.data.slices.map((slice) => (
              <GeneralContent key={slice.id} slice={slice} lede={lede} linkCards={linkCards} />
            ))}
          </div>

          {contactForm ? (
            <div className="mt-8">
              <S9FormWidget
                formUrl={contactForm.formUrl}
                scriptUrl={contactForm.scriptUrl}
                targetElementId="s9-contact-us-form-widget-container"
                height="1200px"
                fallback={content.content_form_unavailable}
              />
            </div>
          ) : null}
        </article>
      </main>

      <Footer />
    </>
  );
}
